/**
 * The runtime config a SPA fetches from its co-located BFF at boot, so ONE
 * env-agnostic build/image self-configures per deploy (per silo) instead of
 * baking the environment into the bundle. Shape mirrors the world-api's public
 * `GET /api/config`.
 */
export interface RuntimeConfig {
  keycloakUrl: string;
  realm: string;
  clientId: string;
  apiUrl: string;
  /**
   * The company this BFF is bound to (single-tenant silo), for the SPA to badge
   * so the user always sees which company they're looking at. Null/absent on a
   * multi-tenant deploy or when the fallback config is used.
   */
  company?: { resourceId: string; organizationId: string } | null;
}

/**
 * Fetch the runtime config from the co-located BFF at startup. The path is
 * ALWAYS same-origin + relative (`/api/config`) — never a baked absolute URL —
 * because the SPA image is env-agnostic and served on the SAME host as its BFF
 * (the whole point: one image, per-silo config). A production build carries prod
 * URLs only as the fallback, used when the endpoint is unreachable or incomplete
 * (e.g. local `nx serve` with no BFF up), so the dev inner loop keeps working
 * while deployed silos get their own config. Call this BEFORE
 * `bootstrapApplication` and feed the result into `provideEsAuth`.
 */
export async function loadRuntimeConfig(
  fallback: RuntimeConfig,
  configPath = '/api/config',
): Promise<RuntimeConfig> {
  try {
    const res = await fetch(configPath, { headers: { accept: 'application/json' } });
    if (res.ok) {
      const cfg = (await res.json()) as Partial<RuntimeConfig>;
      if (cfg.keycloakUrl && cfg.realm && cfg.clientId) {
        return {
          keycloakUrl: cfg.keycloakUrl,
          realm: cfg.realm,
          clientId: cfg.clientId,
          apiUrl: cfg.apiUrl ?? fallback.apiUrl,
          company: cfg.company ?? null,
        };
      }
    }
  } catch {
    // Unreachable (dev without a BFF, or a facade-only deploy) — fall through to the build-time fallback.
  }
  return fallback;
}
